// THE BANNED-OUTPUT REGISTRY — ONE MODULE, SHARED BY THE WORKER AND THE CLIENT (codebook §12; spec §7 T-BAN-1,
// T-BAN-2s, T-BAN-3s).
//
// PROMOTED TO A SHARED MODULE IN R5.5, and that promotion is the point rather than a tidy-up. Through R5.4 this
// registry lived in `client/src/rubric/bannedOutput.ts` and scanned only what the SURFACE rendered. §12 is not a
// rule about the surface: the listed language "MUST NOT appear in any system-authored payload, prompt output, UI
// string, PDF export, log message, or telemetry label." A registry the worker could not reach could not be the
// registry those five other surfaces are held to, and the R5.2 build report flagged exactly that. There is now
// one list, one scanner, and one allowlist; the client re-exports this module rather than keeping a second copy,
// so the two halves of T-BAN-1 cannot disagree about what is banned.
//
// TWO LAYERS, DELIBERATELY.
//
//   (1) `BANNED_TERMS` — closed literal substrings, matched case-insensitively. This is the layer the surface has
//       always had and the layer that must never be loosened. It is deliberately literal: a literal cannot
//       over-match by accident, and the render path fails closed on it.
//
//   (2) `BANNED_PATTERNS` — §12.6 part 1's SEMANTIC layer: inflections, French forms, and the paraphrases the
//       adversarial set probes ("how you stack up", "où vous vous situez"). Authored as regexes with word
//       boundaries, because §12.6 part 3 requires FR and inflection coverage "authored as patterns rather than
//       literal substrings".
//
// THE FALSE-POSITIVE RULE IS AS BINDING AS THE MATCH RULE (§12.6 part 1b): "A false positive here fails the build
// exactly as a missed banned phrase does, because over-matching silently suppresses mandatory copy and silently
// mutilates the applicant's own words." Two consequences are visible in the patterns below and are not
// negotiable:
//
//   * The codebook's evasion set names the bare words "fit" and "alignment". A bare `/\bfit\b/` would fail the
//     product's own registered copy and any CV describing a fitness programme. §12.2 bans them AS CHARACTERIZATIONS
//     OF THE APPLICANT ("alignment (as a characterization of the applicant)" is the codebook's own parenthesis), so
//     the patterns require that context — "a good fit for", "your alignment with" — and leave the bare noun alone.
//   * Near-miss vocabulary used in its legitimate sense is explicitly permitted: "Strong" as a §7.2 Presentation
//     sub-element band, "match" in "CaRMS match cycle", "score" inside a quoted banned-term list. None of the
//     patterns fires on those, and `bannedOutputRegistry.test.ts` pins each of them by name.
//
// WHAT IS NEVER SCANNED: `applicant_verbatim` text (§12.6 part 0). The exemption travels by PATH and by TYPE,
// never by content — `rubricReadPublic.ts`'s pinned two-pattern set on the payload side, `renderContent.tsx`'s
// typed slots on the render side. This module never guesses at provenance; it is handed system-authored text and
// says yes or no.
/** §12.6 part 2 — the allowlist is keyed by COPY-STRING ID, never by free text. */
export const ALLOWLISTED_COPY_IDS: readonly string[] = ['CS-INTENDED-USE', 'CS-INTENDED-USE-SHORT'];

/**
 * The literal registry. Lower-cased, matched case-insensitively as substrings against system-authored text.
 *
 * Grouped by the codebook section that bans each set, so a future edit has to say which rule it is changing.
 */
export const BANNED_TERMS: readonly string[] = [
  // §12.2 — predictive and evaluative language.
  //
  // "competitive" IS NOT A LITERAL HERE, deliberately, and the reason is a real false positive this suite caught.
  // The codebook's own ratified §2.5 vocabulary contains `competitive_selection` and `merit_competitive` — the
  // stated selection basis of an AWARD, a ledger enum — and `factExtract.ts`'s prompt quotes the applicant-side
  // wording "Awarded competitively". §12.2 bans the word as a characterization OF THE APPLICANT; banning the
  // morpheme would fail the extractor's own schema vocabulary and silently mutilate an applicant's "competitive
  // grants office". The concept is carried by the context-bearing patterns below instead.
  'strong candidate',
  'weak candidate',
  'standout',
  'interview likely',
  'interview unlikely',
  'screened out',
  'meets the bar',
  'cut-off',
  'match likelihood',
  'chances',
  'odds',
  'probability of matching',
  'ranked highly',
  'clinical readiness',
  'ready for residency',
  'prepared for practice',
  // §12.2 evasion set (added v1.1).
  'suitability',
  'suited to',
  'compatibility',
  'prospects',
  'application strength',
  'impressive',
  'compelling',
  'well-positioned',
  'realistic target',
  'reach specialty',
  'reach program',
  'safe specialty',
  'safe program',
  'selection likelihood',
  'invitation likelihood',
  'above average',
  'below average',
  'most criteria covered',
  'covers the main things',
  'broadly aligned',
  // §4.2 — the banned top-band superlatives.
  'exceptional',
  'outstanding',
  'distinguished',
  // §4.7 / T-NS-1 — deficiency framing for an absence.
  'you lack',
  'lacks ',
  'deficient',
  'gap in your profile',
  'no experience in',
  'you have not done',
  'absent from your background',
  // §12.1 — score/rank renderings.
  'overall score',
  'total score',
  'percentile',
  'top 10%',
  'compared to other applicants',
  // §12.5 — reviewer/committee simulation and exemplar comparison.
  'as a program director',
  'as a file reviewer',
  'how a reviewer sees',
  'selection committee would',
  'impact score',
  // §12.7 — prohibited fields, as rendered language.
  'impact factor',
  'h-index',
  'citation count',
  'predatory journal',
  // FR forms of the highest-risk terms. The FULL FR set is the pattern layer below (§12.6 part 3 requires FR
  // coverage authored as PATTERNS, not literals, because French inflects); these literals are the FR phrases whose
  // form is fixed and which are therefore cheaper and safer to catch here.
  'candidat fort',
  'candidat faible',
  'exceptionnel',
  'chances de jumelage',
  'probabilité de jumelage',
  'note globale',
  'score global',
  'centile',
  'au-dessus de la moyenne',
  'en dessous de la moyenne',
  'prêt pour la résidence',
];

/**
 * §12.6 part 1 — THE SEMANTIC LAYER: inflections, French forms, and the paraphrases a literal list cannot reach.
 *
 * Every pattern is anchored on word boundaries and every risky one carries the applicant-directed context §12.2
 * actually bans, for the false-positive reason in this file's banner. Each entry names the codebook rule it
 * implements, so an edit has to say which rule it is changing.
 */
/* ---------------------------------------------------------------------------------------------------------- *
 * THE APPLICANT-SUBJECT GRAMMAR (§12.2), factored out because three rules need exactly the same notion of
 * "predicated OF THE APPLICANT" and three hand-written variants of it would drift.
 *
 * R5.5 build-review finding 6: the first cut of the "competitive" patterns keyed on a CLOSED verb list
 * (`is|are|was|were|not|less|more|highly|very|quite`) and therefore let the three most ordinary sentences a
 * generator would actually produce straight through — "Your application looks competitive", "You remain
 * competitive", "Votre candidature semble compétitive". The fix is to stop enumerating adverbs and instead
 * name the SUBJECT (second person, or the applicant / their application as a noun phrase) and the class of
 * COPULAR AND PERCEPTION verbs that predicate an adjective of it. That is the shape §12.2 bans; everything
 * else about the word stays legitimate.
 *
 * WHAT THIS DELIBERATELY STILL PERMITS, and the reason it must (§12.6 part 1b — a false positive fails the
 * build exactly as a miss does):
 *   * `competitive_selection`, `merit_competitive` — ratified §2.5 enum values. `_` is a word character, so
 *     `\bcompetitive\b` has no boundary against it and cannot match inside either token.
 *   * "Awarded competitively" — the adverb. `competitive\b` has no boundary before the `l` of `-ly`.
 *   * "competitive grants office", "a programme described as highly competitive" quoted in APPLICANT text —
 *     the first has no applicant subject at all; the second is exempt by PATH and TYPE, never by content.
 * ---------------------------------------------------------------------------------------------------------- */

/**
 * Second person, or the applicant / a possession of theirs, as the SUBJECT of a predication.
 *
 * `this|the CV` and its siblings are IN the set on purpose: the document under review IS the applicant, in the
 * only sense §12.2 cares about — "This CV is competitive" is a verdict on the person who wrote it. What is NOT in
 * the set, and must not be, is an INSTITUTION: "the residency program", "the department", "this fellowship". Those
 * are the sentences round 2 had to stop rejecting.
 */
const APPLICANT_SUBJECT =
  '(?:you|your\\s+(?:application|candidacy|candidature|profile|cv|r[ée]sum[ée]|file|dossier|portfolio|background|record|standing)' +
  '|(?:the|this)\\s+(?:applicant|candidate)(?:[’\']s)?(?:\\s+(?:application|profile|cv|file))?' +
  '|(?:this|the)\\s+(?:cv|r[ée]sum[ée]|application|candidacy|profile|file|dossier)' +
  '|(?:their|the)\\s+application)';

/** Copular and perception verbs — the verbs that predicate an adjective of a subject. Modals included. */
const PREDICATES =
  '(?:is|are|was|were|isn[’\']t|aren[’\']t|wasn[’\']t|weren[’\']t|looks?|looked|seems?|seemed|appears?|appeared' +
  '|remains?|remained|stays?|stayed|becomes?|became|feels?|felt|reads?|sounds?|sounded|proves?|proved|rates?|rated' +
  '|(?:would|will|could|should|might|may|can|must)\\s+(?:be|remain|stay|look|seem|appear|prove|read)' +
  '|comes?\\s+across)';

/** Optional hedges/intensifiers and an optional `as`, so "looks fairly competitive" / "reads as competitive" hit. */
const HEDGES =
  '(?:\\s+as)?(?:\\s+(?:not|very|quite|highly|fairly|reasonably|less|more|somewhat|broadly|generally|clearly' +
  '|already|still|strongly|barely|only|marginally|pretty|rather|extremely|genuinely|certainly|definitely))*';

/**
 * The FR half of the same set, with the same boundary: `ce CV`, `ce dossier`, `cette candidature` are the
 * applicant; `le programme`, `ce département`, `cette bourse` are not, and "Le programme est compétitif" must
 * pass. Note `candidat`/`candidate` need their determiners kept tight for the same reason.
 */
const APPLICANT_SUBJECT_FR =
  '(?:vous|votre\\s+(?:candidature|dossier|profil|cv|parcours|demande)' +
  '|(?:ce|le)\\s+(?:cv|dossier|profil|curriculum)|(?:cette|la)\\s+candidature' +
  '|(?:le|ce)\\s+candidat|(?:la|cette)\\s+candidate|leur\\s+candidature)';

const PREDICATES_FR =
  // Third person AND the `vous` forms — French's second person is a -ez conjugation, so a rule that only knew
  // `est|sont` could not see "Vous restez compétitif" or even "Vous êtes compétitif", which is the whole
  // second-person surface the FR half of the product renders.
  '(?:est|sont|[ée]tait|[ée]taient|[êée]tes|semble|semblent|semblait|semblez|para[îi]t|paraissent|paraissait|paraissez' +
  '|demeure|demeurent|demeurez|reste|restent|restez|devient|deviennent|devenez|apparaissez|apparaissent' +
  '|s[’\']av[èe]re|s[’\']av[èe]rent|vous\\s+av[èe]rez|se\\s+r[ée]v[èe]le|vous\\s+r[ée]v[èe]lez' +
  '|serez|seriez|resterez|resteriez|semblerez)';

const HEDGES_FR =
  '(?:\\s+(?:pas|tr[èe]s|assez|plut[ôo]t|peu|moins|plus|toujours|d[ée]j[àa]|globalement|clairement|vraiment' +
  '|encore|bien|fort|nettement))*';

export const BANNED_PATTERNS: readonly { id: string; re: RegExp }[] = [
  // §12.2 — "competitive", AS A CHARACTERIZATION. The context is load-bearing, not decorative: see the note on
  // the literal list above. `competitiveness`/`uncompetitive` have no legitimate use and need no context.
  { id: '12.2/competitiveness', re: /\b(un)?competitiveness\b/i },
  { id: '12.2/uncompetitive', re: /\buncompetitive\b/i },
  // ROUND-2 CLOSURE. This pattern used to be a BARE adverb/copula list with no subject at all, so it rejected
  // "The residency program is competitive" — an institution describing itself, which §12.2 does not ban and which
  // the product's own program-character copy legitimately says. It is now anchored to an applicant subject, like
  // its `-of-applicant` sibling; what it still adds over that sibling is the intensifier-only shape with no
  // perception verb ("Your file — highly competitive." reads through the copula-optional branch).
  {
    id: '12.2/competitive-predicated',
    re: new RegExp(
      `\\b${APPLICANT_SUBJECT}(?:\\s+(?:is|are|was|were|isn[’']t|aren[’']t|wasn[’']t|weren[’']t))?` +
      `(?:\\s+(?:not|less|more|highly|very|quite|really|truly|especially|particularly|clearly|certainly))*` +
      `\\s+competitive\\b`,
      'i',
    ),
  },
  // ROUND-3 CLOSURE (verify finding 3). `program|programme` are NOT applicant-attributed nouns. "This is a
  // competitive program." describes the PROGRAM — the same self-description §12.2 has never banned, and which the
  // product's own program-character copy legitimately says; the `-predicated` sibling above was already anchored
  // to an applicant subject for exactly this reason, and leaving the nouns here re-opened the hole one pattern
  // over. Every noun that remains names the applicant or something the applicant submits, so the phrase itself is
  // the attribution; `for` stays, because "competitive for …" has an implied applicant subject.
  { id: '12.2/competitive-attributed', re: /\bcompetitive\s+(for|applicant|candidate|profile|cv|file|application)s?\b/i },
  // THE ORDINARY SENTENCE, which the two patterns above miss: an applicant SUBJECT, a copular or perception
  // verb, and the adjective. "Your application looks competitive." / "You remain competitive." / "You would be
  // competitive at most programs." Anchored on the subject, so nothing without one can match.
  {
    id: '12.2/competitive-of-applicant',
    re: new RegExp(`\\b${APPLICANT_SUBJECT}\\s+${PREDICATES}${HEDGES}\\s+competitive\\b`, 'i'),
  },
  // The same predication with the NOUN rather than the adjective — "your competitiveness", "the applicant's
  // competitiveness" — is already unambiguous and caught by `12.2/competitiveness` above; this one catches the
  // possessive framing that reads as a metric about the applicant.
  {
    id: '12.2/competitiveness-possessed',
    re: /\b(your|their|the\s+applicant[’']?s|this\s+applicant[’']?s)\s+(\w+\s+){0,2}competitiveness\b/i,
  },
  // FR. `compétitivité` is unambiguous; the adjective takes the same context rule as its EN counterpart.
  { id: '12.2/competitiveness-fr', re: /\bcomp[ée]titivit[ée]s?\b/i },
  // ROUND-2 CLOSURE, the FR twin: the bare `est|sont|…` list rejected "Le programme est compétitif". Anchored to
  // the FR applicant subject set, with the copula optional so the intensifier-only shape still lands.
  {
    id: '12.2/competitive-fr',
    re: new RegExp(
      `\\b${APPLICANT_SUBJECT_FR}(?:\\s+(?:est|sont|[ée]tait|[ée]taient|[êée]tes))?${HEDGES_FR}` +
      `\\s+comp[ée]titi\\w*`,
      'i',
    ),
  },
  { id: '12.2/competitive-attributed-fr', re: /\bcomp[ée]titi(f|ve|fs|ves)\s+(pour|comme)\b/i },
  // FR's ordinary sentence — "Votre candidature semble compétitive", "Vous restez compétitif". Same shape as the
  // EN rule: applicant subject, copular/perception verb, adjective in any of its four agreements.
  {
    id: '12.2/competitive-of-applicant-fr',
    re: new RegExp(`\\b${APPLICANT_SUBJECT_FR}\\s+${PREDICATES_FR}${HEDGES_FR}\\s+comp[ée]titi(?:f|ve|fs|ves)\\b`, 'i'),
  },
  // §12.2 — candidate characterization, in any adjective form, EN and FR.
  { id: '12.2/candidate-characterized', re: /\b(strong|weak|excellent|poor|top|solid|outstanding)\s+(candidate|applicant)s?\b/i },
  { id: '12.2/candidate-characterized-fr', re: /\bcandidat(e|s|es)?\s+(fort|forte|faible|excellent|exceptionnel)\w*/i },
  // §12.2 — match/interview likelihood, in any of the ways it can be phrased.
  { id: '12.2/match-likelihood', re: /\b(likelihood|probability|odds|chances?)\s+of\s+(matching|a\s+match|an?\s+interview|being\s+ranked)/i },
  { id: '12.2/match-likelihood-fr', re: /\b(probabilit[ée]|chances?)\s+(de|d[’'])\s*(jumelage|entrevue|appariement)/i },
  { id: '12.2/interview-prediction', re: /\binterviews?\s+(is\s+|are\s+)?(likely|unlikely|guaranteed|assured)\b/i },
  { id: '12.2/screened-out', re: /\b(would|will|may)\s+be\s+screened\s+out\b/i },
  // §12.2 evasion set — the words the codebook bans AS CHARACTERIZATIONS OF THE APPLICANT, and only there.
  { id: '12.2/fit-as-characterization', re: /\b(a|an|good|strong|poor|bad|great|excellent)\s+fit\s+(for|with)\b/i },
  { id: '12.2/alignment-as-characterization', re: /\b(your|their|the\s+applicant[’']?s)\s+alignment\b/i },
  { id: '12.2/readiness', re: /\b(ready|readiness|prepared)\s+(for|to\s+(enter|begin|start))\s+(residency|practice|independent)/i },
  // §12.6 part 1's named adversarial paraphrases — cohort comparison without a comparison word.
  { id: '12.6/stack-up', re: /\b(how|where)\s+you\s+(stack\s+up|rank|sit|stand|compare)\b/i },
  { id: '12.6/relative-to-others', re: /\b(relative|compared)\s+to\s+(others|other\s+applicants|your\s+peers|the\s+cohort)\b/i },
  { id: '12.6/stack-up-fr', re: /\bo[uù]\s+vous\s+vous\s+situez\b/i },
  { id: '12.6/vs-others-fr', re: /\bpar\s+rapport\s+aux\s+autres\s+(candidats|postulants)\b/i },
  // §12.1 — scores, percentiles, ranks and cohort benchmarks, in any of their renderings.
  { id: '12.1/score', re: /\b(overall|total|composite|global|final|quality)\s+scores?\b/i },
  { id: '12.1/score-fr', re: /\b(note|score)\s+(globale?|totale?|d[’']ensemble)\b/i },
  { id: '12.1/percentile', re: /\b(percentile|decile|quartile)s?\b/i },
  { id: '12.1/top-x-percent', re: /\btop\s+\d+\s*%/i },
  { id: '12.1/cohort-benchmark', re: /\b(above|below|at)\s+(the\s+)?(average|median|typical)\s+(applicant|candidate|cv)\b/i },
  { id: '12.1/percent-documented', re: /\b\d+\s*%\s+(documented|complete|covered|aligned)\b/i },
  // §12.1 — weighting disclosure: the formula itself, not the word.
  { id: '12.1/weighting', re: /\bdomains?\s+are\s+weighted\b/i },
  // §12.5 — reviewer simulation, phrased as a role the system adopts.
  { id: '12.5/reviewer-simulation', re: /\bas\s+(a|the)\s+(program\s+director|reviewer|file\s+reviewer|selection\s+committee)\b/i },
  // §4.7 / T-NS-1 — deficiency framing for an absence, in every inflection, EN and FR.
  { id: '4.7/lacking', re: /\b(you|the\s+applicant|your\s+cv)\s+(lack|lacks|lacked|is\s+lacking|are\s+lacking)\b/i },
  { id: '4.7/missing-experience', re: /\b(missing|absent)\s+(experience|background|evidence\s+of\s+you)\b/i },
  { id: '4.7/deficiency-fr', re: /\b(vous\s+manquez|il\s+vous\s+manque|lacune\s+dans\s+votre)\b/i },
  { id: '4.7/weak-area', re: /\b(weakest|strongest|best|worst)\s+(area|domain|section)\b/i },
];

export interface BanScanResult {
  ok: boolean;
  /** The matched term, when a scan fails. Never the surrounding text — a violation is not quoted back out. */
  term?: string;
  /** The §12 rule the SEMANTIC layer matched on, when the failure came from `BANNED_PATTERNS` rather than a literal. */
  pattern_id?: string;
}

/**
 * Scan SYSTEM-AUTHORED text. `copyId` is consulted only to honour the §12.6 part-2 allowlist, which is keyed by
 * id: `CS-INTENDED-USE` is the sole permitted occurrence of its own excluded-construct vocabulary in the product,
 * and it is permitted BECAUSE OF ITS ID, not because of what it says.
 */
export function scanSystemText(text: string, copyId?: string): BanScanResult {
  if (copyId !== undefined && ALLOWLISTED_COPY_IDS.includes(copyId)) return { ok: true };
  const haystack = text.toLowerCase();
  for (const term of BANNED_TERMS) {
    if (haystack.includes(term)) return { ok: false, term };
  }
  // The semantic layer runs against the ORIGINAL text, not the lower-cased copy: every pattern carries its own
  // `i` flag, and lower-casing first would quietly change what a future case-sensitive pattern means.
  for (const { id, re } of BANNED_PATTERNS) {
    const m = re.exec(text);
    if (m) return { ok: false, term: m[0], pattern_id: id };
  }
  return { ok: true };
}
